import React from "react";
import { Toaster } from "sonner";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { AdaptiveSidebar } from "./AdaptiveSidebar";
import { Breadcrumb } from "./Breadcrumb";
import { TourOverlay } from "../demo/TourOverlay";
import { TourCompletionCard } from "../demo/TourCompletionCard";
import { DemoModeBanner } from "../demoMode/DemoModeUI";
import InsightToggle from "../../insight/InsightToggle";
import "../operationalBase.css";

/**
 * OperationalShell — the dark luxury wrapper that frames every Operational
 * Base surface (Phase 2 home; future phases will reuse it for module pages).
 *
 * Layout per design_guidelines.md §3.1:
 *
 *   ┌────────────────────────────────────────────────────────┐
 *   │  Adaptive sidebar │  top context bar (breadcrumb)       │
 *   │   (capability     │  ──────────────────────────────────  │
 *   │    driven)        │  main content (children)            │
 *   │                   │                                      │
 *   └────────────────────────────────────────────────────────┘
 *
 * Same shell across all roles — what changes is the capability map's
 * `visible_modules`, which the sidebar and main content read from.
 * This is the L5 "same shell, adaptive surfaces" principle made tangible.
 */
export const OperationalShell = ({ children, "data-testid": testId = "ob-shell" }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div
                data-testid="ob-shell-loading"
                className="ob-shell flex items-center justify-center"
                style={{ minHeight: "100vh" }}
            >
                <p style={{ color: "var(--ob-text-tertiary)" }} className="text-sm">
                    Loading your environment…
                </p>
            </div>
        );
    }
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return (
        <div data-testid={testId} className="ob-shell">
            <AdaptiveSidebar />
            <main
                className="lg:ml-64 min-h-screen"
                style={{ paddingTop: "0" }}
            >
                <DemoModeBanner />

                {/* Top context bar — breadcrumb only. No menus, no widgets. */}
                <header
                    className="sticky top-0 z-20 backdrop-blur-2xl"
                    style={{
                        background: "rgba(11, 16, 24, 0.75)",
                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                    }}
                >
                    <div className="px-6 sm:px-8 lg:px-12 h-14 flex items-center justify-between">
                        <div className="ml-12 lg:ml-0">
                            <Breadcrumb />
                        </div>
                        <div className="flex items-center">
                            <InsightToggle variant="dark" />
                        </div>
                    </div>
                </header>

                {/* Main canvas */}
                <div className="px-6 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-7xl mx-auto">
                    {children}
                </div>
            </main>

            <TourOverlay />
            <TourCompletionCard />

            <Toaster
                position="top-right"
                toastOptions={{
                    style: {
                        background: "rgba(22,28,40,0.95)",
                        color: "var(--ob-text-primary)",
                        border: "1px solid rgba(255,255,255,0.1)",
                    },
                }}
            />
        </div>
    );
};
